import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { GlassCard } from '@/components/ui/GlassCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { formatCurrency, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { UserConfig, Month } from '@/types';

const MONTH_NAMES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

export default function Config() {
  const [config, setConfig] = useState<UserConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<UserConfig>>({});

  const [months, setMonths] = useState<Month[]>([]);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<UserConfig>('/config')
      .then((data) => {
        setConfig(data);
        setForm(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const fetchMonths = () => {
    setMonthsLoading(true);
    api
      .get<Month[]>('/months')
      .then(setMonths)
      .catch(console.error)
      .finally(() => setMonthsLoading(false));
  };

  useEffect(() => {
    fetchMonths();
  }, []);

  const handleDelete = async (id: string, fileName: string) => {
    if (
      !confirm(
        `¿Eliminar "${fileName}"?\n\nEsta acción borrará todos los días, SKUs, pagos y anomalías del mes. NO se puede deshacer.`,
      )
    ) {
      return;
    }
    setDeletingId(id);
    try {
      await api.delete(`/months/${id}`);
      toast.success(`"${fileName}" eliminado`);
      fetchMonths();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al eliminar');
    } finally {
      setDeletingId(null);
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // Strip server-managed fields (id, updatedAt) so the backend whitelist accepts it
      const { id: _id, updatedAt: _u, ...payload } = form;
      void _id; void _u;
      const updated = await api.patch<UserConfig>('/config', payload);
      setConfig(updated);
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    if (!confirm('¿Restablecer todos los valores por defecto?')) return;
    try {
      const updated = await api.post<UserConfig>('/config/reset');
      setConfig(updated);
      setForm(updated);
      toast.success('Configuración restablecida');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="material-symbols-outlined text-5xl text-primary animate-spin">
          progress_activity
        </span>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        tag="// MÓDULO :: 07 / CONFIG"
        title="Configuración"
        subtitle="Precios, costos, umbrales y gestión de meses"
        action={
          <div className="flex gap-2">
            <button onClick={onReset} className="kinetic-button-outline">
              <span className="material-symbols-outlined text-base">restart_alt</span>
              Restablecer
            </button>
            <button onClick={onSave} disabled={saving} className="kinetic-button">
              {saving && <span className="material-symbols-outlined animate-spin">progress_activity</span>}
              <span className="material-symbols-outlined">save</span>
              Guardar
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <GlassCard variant="kinetic" padding="md" rounded="none">
          <div className="mb-4 pb-3 border-b-2 border-outline">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              // PRICING
            </p>
            <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
              Precios y Costos
            </h3>
          </div>
          <div className="space-y-5">
            <NumField
              label="Precio unitario"
              value={form.precioUnitario}
              onChange={(v) => setForm((f) => ({ ...f, precioUnitario: v }))}
              prefix="$"
              hint="Precio de venta por empanada"
            />
            <NumField
              label="Costo unitario"
              value={form.costoUnitario}
              onChange={(v) => setForm((f) => ({ ...f, costoUnitario: v }))}
              prefix="$"
              hint="Costo de producción por empanada"
            />
            <NumField
              label="Costos fijos mensuales"
              value={form.costosFijosMensuales}
              onChange={(v) => setForm((f) => ({ ...f, costosFijosMensuales: v }))}
              prefix="$"
              hint="Renta, servicios, sueldos, etc."
            />
            <NumField
              label="Fondo fijo de caja"
              value={form.fondoFijo}
              onChange={(v) => setForm((f) => ({ ...f, fondoFijo: v }))}
              prefix="$"
              hint="Efectivo inicial de cada turno"
            />
          </div>
        </GlassCard>

        <GlassCard variant="kinetic" padding="md" rounded="none">
          <div className="mb-4 pb-3 border-b-2 border-outline">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              // INVENTORY
            </p>
            <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
              Inventario
            </h3>
          </div>
          <div className="space-y-5">
            <NumField
              label="Lead Time (días)"
              value={form.leadTimeDias}
              onChange={(v) => setForm((f) => ({ ...f, leadTimeDias: v }))}
              hint="Tiempo de reposición de proveedores (entero)"
              integer
            />
            <NumField
              label="Safety Stock Multiplier"
              value={form.safetyStockMultiplier}
              onChange={(v) => setForm((f) => ({ ...f, safetyStockMultiplier: v }))}
              hint="Días de venta como stock de seguridad (entero)"
              integer
            />
          </div>
        </GlassCard>

        <GlassCard variant="kinetic" padding="md" rounded="none" className="lg:col-span-2">
          <div className="mb-4 pb-3 border-b-2 border-outline">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              // THRESHOLDS
            </p>
            <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
              Umbrales de Detección
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NumField
              label="Umbral cash variance (MXN)"
              value={form.umbralCashVariance}
              onChange={(v) => setForm((f) => ({ ...f, umbralCashVariance: v }))}
              prefix="$"
              hint="Diferencia mínima para alertar"
            />
            <NumField
              label="Umbral cash variance (%)"
              value={form.umbralCashVariancePct}
              onChange={(v) => setForm((f) => ({ ...f, umbralCashVariancePct: v }))}
              suffix="%"
              hint="Porcentaje de diferencia"
            />
          </div>
        </GlassCard>
      </div>

      {/* ===== Gestión de Meses Cargados ===== */}
      <section className="mt-6">
        <GlassCard variant="kinetic" padding="md" rounded="none" className="overflow-hidden">
          <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-outline">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // MONTH_MANAGEMENT
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Gestión de Meses Cargados
              </h3>
              <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                // Elimina meses para liberar espacio o re-procesarlos
              </p>
            </div>
            <button
              onClick={fetchMonths}
              className="kinetic-button-outline"
              disabled={monthsLoading}
            >
              <span
                className={cn(
                  'material-symbols-outlined text-base',
                  monthsLoading && 'animate-spin',
                )}
              >
                refresh
              </span>
              Actualizar
            </button>
          </div>

          {monthsLoading && months.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <span className="material-symbols-outlined text-4xl text-primary animate-spin">
                progress_activity
              </span>
            </div>
          ) : months.length === 0 ? (
            <div className="text-center py-12">
              <span className="material-symbols-outlined text-5xl text-on-surface-variant opacity-50">
                inventory_2
              </span>
              <p className="mt-2 text-on-surface-variant opacity-70">
                No hay meses cargados aún
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-outline">
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      Período
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      Archivo
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                      Días
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                      Unidades
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                      Ingresos
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-center">
                      DQS
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-center">
                      Estado
                    </th>
                    <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => {
                    const dqs = Number(m.avgDqs);
                    const dqsOk = dqs >= 50;
                    const dqsWarn = dqs >= 20;
                    const isDeleting = deletingId === m.id;
                    return (
                      <tr
                        key={m.id}
                        className={cn(
                          'border-b border-outline/50 transition-opacity',
                          isDeleting && 'opacity-50',
                        )}
                      >
                        <td className="px-3 py-2 font-bold num font-condensed text-lg text-text-bright">
                          {MONTH_NAMES[m.month - 1]} {m.year}
                        </td>
                        <td className="px-3 py-2 text-sm text-text-bright font-mono">
                          {m.fileName}
                        </td>
                        <td className="px-3 py-2 text-right num text-text-bright">
                          {m.totalDays}
                        </td>
                        <td className="px-3 py-2 text-right num font-bold text-text-bright">
                          {formatNumber(m.totalUnits)}
                        </td>
                        <td className="px-3 py-2 text-right num font-bold text-primary">
                          {formatCurrency(Number(m.totalRevenue))}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span
                            className={cn(
                              'inline-block px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest font-bold border-2 border-black',
                              dqsOk
                                ? 'bg-tertiary text-black'
                                : dqsWarn
                                  ? 'bg-warning text-black'
                                  : 'bg-error text-white',
                            )}
                          >
                            {dqs.toFixed(1)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span
                            className={cn(
                              'inline-block px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest font-bold border-2 border-black',
                              m.status === 'PROCESSED'
                                ? 'bg-primary text-black'
                                : 'bg-warning text-black',
                            )}
                          >
                            {m.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => handleDelete(m.id, m.fileName)}
                            disabled={isDeleting}
                            className="kinetic-button-danger"
                            title="Eliminar mes y todos sus datos"
                          >
                            {isDeleting ? (
                              <span className="material-symbols-outlined text-base animate-spin">
                                progress_activity
                              </span>
                            ) : (
                              <span className="material-symbols-outlined text-base">delete</span>
                            )}
                            Borrar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>
      </section>
    </>
  );
}

interface NumFieldProps {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  hint?: string;
}

interface NumFieldProps {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  hint?: string;
  step?: number;
  integer?: boolean;
}

function NumField({ label, value, onChange, prefix, suffix, hint, step = 0.01, integer }: NumFieldProps) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
        // {label}
      </label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-primary font-bold font-mono">
            {prefix}
          </span>
        )}
        <input
          type="number"
          step={integer ? 1 : step}
          min={integer ? 0 : undefined}
          value={value ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange(0);
              return;
            }
            const parsed = integer ? parseInt(raw, 10) : parseFloat(raw);
            onChange(Number.isNaN(parsed) ? 0 : parsed);
          }}
          className={`kinetic-input num ${prefix ? 'pl-9' : 'pl-4'} ${
            suffix ? 'pr-9' : 'pr-4'
          }`}
        />
        {suffix && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-primary font-bold font-mono">
            {suffix}
          </span>
        )}
      </div>
      {hint && (
        <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-2">
          // {hint}
        </p>
      )}
    </div>
  );
}
